import React, { useState } from 'react';
import './ContentDisplay.css';

const ContentDisplay = ({ items, loading, error }) => {
  const [failedImages, setFailedImages] = useState(new Set());

  if (loading) {
    return (
      <div className="content-grid loading-state">
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading Paul's latest interests...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-grid error-state">
        <div className="error">
          {error}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="content-grid empty-state">
        <div className="empty-content">
          <h3>No content yet!</h3>
          <p>Add some content to get started</p>
        </div>
      </div>
    );
  }

  const handleImageError = (itemId) => {
    setFailedImages(prev => new Set(prev).add(itemId));
  };

  const renderContentCard = (item, index) => {
    const itemId = item.id || index;
    const hasImageFailed = failedImages.has(itemId);

    return (
      <div key={itemId} className={`content-card ${item.type}-card`}>
        <div className="card-content">
          {item.type === 'image' && !hasImageFailed ? (
            <img 
              src={item.content} 
              alt="Paul likes this" 
              className="card-image"
              onError={() => handleImageError(itemId)}
              loading="lazy"
            />
          ) : item.type === 'image' && hasImageFailed ? (
            <div className="image-error">
              <div className="error-icon">🖼️</div>
              <div className="error-text">Image unavailable</div>
              <div className="error-url">{truncateUrl(item.content)}</div>
            </div>
          ) : item.type === 'link' ? (
            <a 
              href={item.content} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="card-link"
            >
              {item.preview ? (
                <div className="link-preview">
                  {item.preview.image && (
                    <div className="preview-image-container">
                      <img 
                        src={item.preview.image} 
                        alt="" 
                        className="preview-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="preview-content">
                    <div className="preview-header">
                      {item.preview.favicon && (
                        <img 
                          src={item.preview.favicon} 
                          alt="" 
                          className="preview-favicon"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="preview-domain">{item.preview.domain}</div>
                    </div>
                    <div className="preview-title">{item.preview.title}</div>
                    {item.preview.description && (
                      <div className="preview-description">{item.preview.description}</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="link-fallback">
                  <div className="link-icon">🔗</div>
                  <div className="link-title">{extractDomain(item.content)}</div>
                  <div className="link-url">{truncateUrl(item.content)}</div>
                </div>
              )}
            </a>
          ) : (
            <div className="card-text">
              {item.content}
            </div>
          )}
          
          {item.caption && (
            <div className="card-caption">
              {item.caption}
            </div>
          )}
        </div>
        
        <div className="card-footer">
          <div className="timestamp">
            {formatTimestamp(item.timestamp)}
          </div>
        </div>
      </div>
    );
  };

  const truncateUrl = (url) => {
    if (url.length > 40) {
      return url.substring(0, 37) + '...';
    }
    return url;
  };

  const extractDomain = (url) => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace('www.', '');
    } catch {
      return 'Link';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="content-grid">
      {items.map((item, index) => renderContentCard(item, index))}
    </div>
  );
};

export default ContentDisplay;